<template>
  <div class="client-filter-content">
    <!-- Single-column layout for filters -->
    <quick-filter-panel
      ref="filterPanel"
      :fields="filterFields"
      :values="filterValues"
      title="Filter Clients"
      :show-header="false"
      @update="handleFilterUpdate"
      @clear="clearFilters"
    />
  </div>
</template>

<script>
import QuickFilterPanel from '@/tools/components/filter/QuickFilterPanel.vue'

/**
 * ClientFilterContent - Filter form fields for Organization view
 *
 * Uses QuickFilterPanel for a unified single-column layout.
 * All filters are configured via the filterFields computed property.
 *
 * Props:
 * - All filter values as selected*Filter props
 * - All filter options as *FilterOptions props
 *
 * Events:
 * - update:*-filter - Emitted when filter value changes
 * - clear-all - Emitted when all filters are cleared
 */
export default {
  name: 'ClientFilterContent',
  components: {
    QuickFilterPanel,
  },
  props: {
    // Selected filter values
    selectedStatusFilter: {
      type: String,
      default: null,
    },
    selectedTimesheetFilter: {
      type: String,
      default: null,
    },
    selectedConfigFilter: {
      type: String,
      default: null,
    },
    selectedPublisherFilter: {
      type: String,
      default: null,
    },
    selectedDiscrepancyFilter: {
      type: String,
      default: null,
    },
    selectedPacingFilter: {
      type: String,
      default: null,
    },
    selectedYieldFilter: {
      type: String,
      default: null,
    },
    selectedAdxHealthFilter: {
      type: String,
      default: null,
    },
    selectedNetworkHealthFilter: {
      type: String,
      default: null,
    },

    // Filter options
    statusFilterOptions: {
      type: Array,
      default: () => [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
    },
    timesheetFilterOptions: {
      type: Array,
      default: () => [
        { text: 'Has Timesheet', value: 'has-timesheet' },
        { text: 'No Timesheet', value: 'no-timesheet' },
      ],
    },
    configFilterOptions: {
      type: Array,
      default: () => [
        { text: 'Has Config', value: 'has-config' },
        { text: 'No Config', value: 'no-config' },
      ],
    },
    publisherFilterOptions: {
      type: Array,
      default: () => [
        { text: 'Has Publisher', value: 'has-publisher' },
        { text: 'No Publisher', value: 'no-publisher' },
      ],
    },
    discrepancyFilterOptions: {
      type: Array,
      default: () => [
        { text: 'Has Discrepancy', value: 'has-discrepancy' },
        { text: 'No Discrepancy', value: 'no-discrepancy' },
      ],
    },
    pacingFilterOptions: {
      type: Array,
      default: () => [
        { text: 'Has Pacing', value: 'has-pacing' },
        { text: 'No Pacing', value: 'no-pacing' },
      ],
    },
    yieldFilterOptions: {
      type: Array,
      default: () => [
        { text: 'Has Yield', value: 'has-yield' },
        { text: 'No Yield', value: 'no-yield' },
      ],
    },
    adxHealthFilterOptions: {
      type: Array,
      default: () => [
        { text: 'Has AdX Health', value: 'has-adx-health' },
        { text: 'No AdX Health', value: 'no-adx-health' },
      ],
    },
    networkHealthFilterOptions: {
      type: Array,
      default: () => [
        { text: 'Has Network Health', value: 'has-network-health' },
        { text: 'No Network Health', value: 'no-network-health' },
      ],
    },
  },
  computed: {
    /**
     * Filter field configuration for QuickFilterPanel
     */
    filterFields() {
      return [
        // Client Properties
        {
          key: 'status',
          label: 'Status',
          icon: ['fas', 'circle-check'],
          items: this.statusFilterOptions,
          multiple: false,
        },
        {
          key: 'timesheet',
          label: 'Timesheet',
          icon: ['fas', 'clock'],
          items: this.timesheetFilterOptions,
          multiple: false,
        },
        {
          key: 'config',
          label: 'Configuration',
          icon: ['fas', 'cog'],
          items: this.configFilterOptions,
          multiple: false,
        },
        {
          key: 'publisher',
          label: 'Publisher',
          icon: ['fas', 'network-wired'],
          items: this.publisherFilterOptions,
          multiple: false,
        },
        // Report Availability
        {
          key: 'discrepancy',
          label: 'Discrepancy',
          icon: ['fas', 'chart-line'],
          items: this.discrepancyFilterOptions,
          multiple: false,
        },
        {
          key: 'pacing',
          label: 'Pacing',
          icon: ['fas', 'gauge-high'],
          items: this.pacingFilterOptions,
          multiple: false,
        },
        {
          key: 'yield',
          label: 'Yield',
          icon: ['fas', 'chart-pie'],
          items: this.yieldFilterOptions,
          multiple: false,
        },
        {
          key: 'adxHealth',
          label: 'AdX Health',
          icon: ['fas', 'heart-pulse'],
          items: this.adxHealthFilterOptions,
          multiple: false,
        },
        {
          key: 'networkHealth',
          label: 'Network Health',
          icon: ['fas', 'heart-circle-check'],
          items: this.networkHealthFilterOptions,
          multiple: false,
        },
      ]
    },

    /**
     * Current filter values mapped to QuickFilterPanel format
     */
    filterValues() {
      return {
        status: this.selectedStatusFilter,
        timesheet: this.selectedTimesheetFilter,
        config: this.selectedConfigFilter,
        publisher: this.selectedPublisherFilter,
        discrepancy: this.selectedDiscrepancyFilter,
        pacing: this.selectedPacingFilter,
        yield: this.selectedYieldFilter,
        adxHealth: this.selectedAdxHealthFilter,
        networkHealth: this.selectedNetworkHealthFilter,
      }
    },
  },
  methods: {
    /**
     * Handle filter updates from QuickFilterPanel
     * QuickFilterPanel emits the full values object, not (key, value) pairs
     */
    handleFilterUpdate(newValues) {
      // Map field keys to emit event names
      const eventMap = {
        status: 'update:status-filter',
        timesheet: 'update:timesheet-filter',
        config: 'update:config-filter',
        publisher: 'update:publisher-filter',
        discrepancy: 'update:discrepancy-filter',
        pacing: 'update:pacing-filter',
        yield: 'update:yield-filter',
        adxHealth: 'update:adx-health-filter',
        networkHealth: 'update:network-health-filter',
      }

      // Emit changes for each filter key
      Object.entries(newValues || {}).forEach(([key, value]) => {
        if (eventMap[key]) {
          this.$emit(eventMap[key], value)
        }
      })
    },

    /**
     * Clear all filters
     */
    clearFilters() {
      this.$emit('update:status-filter', null)
      this.$emit('update:timesheet-filter', null)
      this.$emit('update:config-filter', null)
      this.$emit('update:publisher-filter', null)
      this.$emit('update:discrepancy-filter', null)
      this.$emit('update:pacing-filter', null)
      this.$emit('update:yield-filter', null)
      this.$emit('update:adx-health-filter', null)
      this.$emit('update:network-health-filter', null)
      this.$emit('clear-all')
    },
  },
}
</script>

<style scoped lang="scss">
.client-filter-content {
  padding: 8px 0;
}
</style>
